/**
 * Reglas fiscales del alta de Aliados (US-2.2) — lógica pura, sin UI ni antd.
 *
 * Fuente de verdad: `docs/GoElectricVault/02-Modulos/Identidad-Fiscal-Aliado.md`.
 *
 * Reglas duras (no reinterpretar):
 * - El DV se ata al TIPO DE DOCUMENTO, no al tipo de persona: toda identificación con
 *   NIT lleva DV (jurídica y natural inscrita en RUT); CC/CE no llevan.
 * - El DV es un dígito 0–9 y el 0 es válido. Alcance: solo forma.
 * - El DV NUNCA se recalcula ni se valida por fórmula: no hay acto público de la DIAN
 *   que fije el algoritmo. NO agregar la validación módulo 11 "para completar el hueco":
 *   la razón de no validar es normativa, no una omisión (vault §3).
 */
import type {
  AllyDocumentType,
  CreateAllyBody,
  PersonType,
  UpdateAllyBody,
} from '@goelectric/shared-types';

/** Fila de administrador en el formulario (parcial mientras se tipea; el contrato exige ambos). */
export interface CreateAllyAdminState {
  name?: string;
  email?: string;
}

/** Estado del formulario de alta necesario para armar el cuerpo del contrato. */
export interface CreateAllyFormState {
  personType: PersonType;
  /** Para JURIDICA el builder fuerza `NIT`; para NATURAL lo toma tal cual. */
  documentType: AllyDocumentType;
  documentNumber: string;
  /** DV del NIT. Ausente en CC/CE. Se descarta si el documento no es NIT. */
  checkDigit?: number | null;
  /**
   * Solo persona natural: si está inscrita en el RUT se identifica con **NIT** (número = su
   * cédula) + DV; si no, con su CC/CE y sin DV. Reemplaza a `NIT` en el desplegable: el
   * operador declara la inscripción, no un tipo de documento (§2 del vault).
   */
  registeredInRut?: boolean;
  legalName?: string;
  /** Solo persona natural: nombres y apellidos viajan por separado (vault §1). */
  firstName?: string;
  lastName?: string;
  email: string;
  phone?: string;
  commissionRatePct: number;
  /** Administradores locales (bloque opcional). */
  admins?: CreateAllyAdminState[];
  /** Solo persona natural: el titular precargado como primer administrador. */
  ownerIsAdmin?: boolean;
}

/** Motivos por los que un documento compuesto no se puede partir sin adivinar. */
export type CompoundDocumentError =
  | 'EMPTY'
  /** No hay separador `-`, o hay más de uno. */
  | 'NO_SEPARATOR'
  /** La parte del número no son solo dígitos. */
  | 'INVALID_NUMBER'
  /** El DV no es un único dígito 0–9. */
  | 'INVALID_CHECK_DIGIT';

/** Resultado explícito del parseo: o trae número + DV, o dice qué falló. */
export type ParseCompoundDocumentResult =
  | { ok: true; documentNumber: string; checkDigit: number }
  | { ok: false; error: CompoundDocumentError };

const NUMBER_RE = /^\d+$/;
const CHECK_DIGIT_RE = /^[0-9]$/;

/**
 * Separa el documento compuesto que muestra el RUT (`901234567-8`) en número + DV.
 * No adivina: sin separador, o con caracteres raros, devuelve `{ ok: false, error }`.
 * NO valida el VALOR del DV (vault §3), solo su forma; el número se conserva como texto
 * (no se convierte a número, para no perder ceros a la izquierda).
 */
export function parseCompoundDocument(input: string): ParseCompoundDocumentResult {
  const text = input.trim();
  if (text === '') {
    return { ok: false, error: 'EMPTY' };
  }

  const parts = text.split('-');
  if (parts.length !== 2) {
    return { ok: false, error: 'NO_SEPARATOR' };
  }

  const [rawNumber = '', rawDigit = ''] = parts;
  const documentNumber = rawNumber.trim();
  const checkDigitText = rawDigit.trim();

  if (!NUMBER_RE.test(documentNumber)) {
    return { ok: false, error: 'INVALID_NUMBER' };
  }
  if (!CHECK_DIGIT_RE.test(checkDigitText)) {
    return { ok: false, error: 'INVALID_CHECK_DIGIT' };
  }

  return { ok: true, documentNumber, checkDigit: Number(checkDigitText) };
}

/**
 * Arma el cuerpo de `POST /api/v1/admin/allies` (`CreateAllyBody`) desde el estado del
 * formulario.
 *
 * El DV viaja **si y solo si** el documento resultante es NIT: para CC/CE la clave se
 * omite por completo (ni `undefined` ni el centinela `0`), igual que hace el mapper de
 * respuesta del backend. La requiredad de `legalName`/`firstName`/`lastName`/`checkDigit`
 * la valida el formulario (paso siguiente); acá solo se compone el contrato.
 */
export function buildCreateAllyPayload(state: CreateAllyFormState): CreateAllyBody {
  const checkDigit = state.checkDigit ?? undefined;
  const admins = buildAdminsPayload(state.admins);
  const common = {
    documentNumber: state.documentNumber,
    email: state.email,
    commissionRatePct: state.commissionRatePct,
    ...(state.phone ? { phone: state.phone } : {}),
    // Bloque opcional: sin administradores la clave se omite (contrato `admins?`).
    ...(admins.length > 0 ? { admins } : {}),
  };

  if (state.personType === 'JURIDICA') {
    return {
      personType: 'JURIDICA',
      documentType: 'NIT', // fijo: la persona jurídica se identifica con NIT (vault §4)
      legalName: state.legalName,
      ...(checkDigit !== undefined ? { checkDigit } : {}),
      ...common,
    };
  }

  return {
    personType: 'NATURAL',
    // La traducción CC/CE + RUT → NIT vive acá: el formulario guarda lo que el operador
    // eligió (CC/CE + un booleano) y el contrato sigue recibiendo NIT + DV (vault §2).
    documentType: state.registeredInRut ? 'NIT' : state.documentType,
    firstName: state.firstName,
    lastName: state.lastName,
    ...(state.registeredInRut && checkDigit !== undefined ? { checkDigit } : {}),
    ...common,
  };
}

/**
 * Arma el cuerpo del `PATCH /api/v1/admin/allies/{allyId}` (edición, TG-118) desde el mismo
 * estado del formulario. Se manda el **set completo de campos editables** (no un diff): el
 * backend valida la consistencia fiscal sobre el estado resultante y recalcula el DV, así
 * que reusar el builder del alta evita duplicar la traducción CC/CE+RUT→NIT y el omitir el
 * DV cuando no aplica. `admins`/`ownerIsAdmin` no entran (no son editables acá).
 */
export function buildUpdateAllyPayload(state: CreateAllyFormState): UpdateAllyBody {
  return buildCreateAllyPayload(state);
}

/**
 * Normaliza la fila del formulario al `{ email, name }` del contrato y descarta las
 * incompletas. El formulario ya exige ambos campos; esto blinda el contrato ante una
 * fila agregada y no completada (`add({})`).
 */
function buildAdminsPayload(
  admins: CreateAllyAdminState[] | undefined,
): { email: string; name: string }[] {
  return (admins ?? [])
    .map((admin) => ({ name: admin.name?.trim() ?? '', email: admin.email?.trim() ?? '' }))
    .filter((admin) => admin.email !== '' && admin.name !== '');
}
